/**
 * product-media-slider.js
 *
 * Card media slider for `product-grid-item.liquid` (media slider mode).
 *
 * Behaviour
 *  - Desktop (hover: hover): the featured image is the resting state. Hovering the
 *    card media fades in the slider and autoplays through all product media, with a
 *    progress bar at the bottom acting as pagination (one segment per slide, the
 *    active one filling over the slide duration). Leaving resets back to slide 1.
 *  - Touch: the slider is visible and swiping left / right changes slides without
 *    opening the product page.
 *  - Video / external-video slides are paused while off-screen and played when active.
 *  - Honours `prefers-reduced-motion`.
 *
 * Markup contract (rendered by the snippet):
 *  <product-media-slider class="pgi-slider" data-pgi-slider data-duration="2500">
 *    <div class="pgi-slider__slide is-active" data-pgi-slide>…</div>
 *    …
 *    <div class="pgi-slider__progress" data-pgi-progress>
 *      <span class="pgi-slider__segment"><span class="pgi-slider__fill"></span></span>
 *      …
 *    </div>
 *  </product-media-slider>
 *
 * Add to the theme with:
 *  {{ 'product-media-slider.js' | asset_url | script_tag }}
 */
(function () {
  'use strict';

  if (window.__pgiMediaSliderLoaded) return;
  window.__pgiMediaSliderLoaded = true;

  var INITIALIZED = 'pgiSliderReady';
  var SWIPE_THRESHOLD = 30;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover = window.matchMedia('(hover: hover)').matches;
  var hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  function setupSlider(stage) {
    if (!stage || stage.dataset[INITIALIZED] === 'true') return;
    stage.dataset[INITIALIZED] = 'true';

    var slides = Array.prototype.slice.call(stage.querySelectorAll('[data-pgi-slide]'));
    if (slides.length < 2) return;

    var segments = Array.prototype.slice.call(stage.querySelectorAll('.pgi-slider__segment'));
    var duration = parseInt(stage.dataset.duration, 10) || 2500;
    var card = stage.closest('.product-item__image') || stage.parentNode;
    var link = stage.closest('a');
    var idx = 0;
    var timer = null;

    function playMedia(slide, shouldPlay) {
      var video = slide.querySelector('video');
      if (!video) return;
      try {
        if (shouldPlay) {
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        } else {
          video.pause();
        }
      } catch (e) {
        /* autoplay restrictions — nothing to do */
      }
    }

    function activate(i) {
      idx = ((i % slides.length) + slides.length) % slides.length;

      slides.forEach(function (slide, si) {
        var isActive = si === idx;
        slide.classList.toggle('is-active', isActive);
        playMedia(slide, isActive);
      });

      if (!segments.length) return;

      segments.forEach(function (seg, si) {
        seg.classList.remove('is-active', 'is-done');
        if (si < idx) seg.classList.add('is-done');
      });

      var current = segments[idx];
      if (current) {
        // Restart the fill animation from zero.
        void current.offsetWidth;
        current.classList.add('is-active');
      }
    }

    function start() {
      if (reduced) return;
      activate(0);
      clearInterval(timer);
      timer = setInterval(function () {
        var next = (idx + 1) % slides.length;
        if (next === 0 && segments.length) {
          segments.forEach(function (seg) { seg.classList.remove('is-done'); });
        }
        activate(next);
      }, duration);
    }

    function stop() {
      clearInterval(timer);
      timer = null;
      slides.forEach(function (slide, si) {
        slide.classList.toggle('is-active', si === 0);
        playMedia(slide, false);
      });
      segments.forEach(function (seg) { seg.classList.remove('is-active', 'is-done'); });
      idx = 0;
    }

    if (canHover && card) {
      card.addEventListener('mouseenter', start);
      card.addEventListener('mouseleave', stop);
      card.addEventListener('focusin', start);
      card.addEventListener('focusout', function (event) {
        if (!card.contains(event.relatedTarget)) stop();
      });
    }

    if (hasTouch) {
      // Reflect the first slide in the progress pips right away.
      activate(0);

      var startX = 0;
      var startY = 0;
      var tracking = false;
      var swiped = false;

      stage.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        tracking = true;
        swiped = false;
      }, { passive: true });

      stage.addEventListener('touchend', function (event) {
        if (!tracking) return;
        tracking = false;

        var touch = event.changedTouches[0];
        if (!touch) return;

        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;

        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          activate(dx < 0 ? idx + 1 : idx - 1);
        }
      }, { passive: true });

      // A swipe should change the image, not open the product page.
      if (link) {
        link.addEventListener('click', function (event) {
          if (!swiped) return;
          event.preventDefault();
          swiped = false;
        });
      }
    }
  }

  function init(root) {
    var scope = root || document;
    if (scope.nodeType !== 1 && scope.nodeType !== 9 && scope.nodeType !== 11) return;

    if (scope.nodeType === 1 && scope.matches && scope.matches('[data-pgi-slider]')) {
      setupSlider(scope);
    }

    var sliders = scope.querySelectorAll ? scope.querySelectorAll('[data-pgi-slider]') : [];
    Array.prototype.forEach.call(sliders, setupSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Re-init for grids swapped in by filtering / infinite scroll / section rendering.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1) init(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
